import Field from "@components/inputs/field";
import { IInputOnBlurEvent } from "@components/inputs/input";
import { IMultiSelectionChangeArgs } from "@components/inputs/select/MultiSelect";
import { IReadOnlyListItem } from "@components/readOnlyList/ReadOnlyListItem";
import { ISmartFieldRenderProps } from "@components/smart/smartField/SmartField";
import { ISmartFieldEvents } from "@components/smart/smartField/SmartFieldUtils";
import SmartPairingMultiSelect from "@components/smart/smartPairingSelect/SmartPairingMultiSelect";
import { fetchItemsByInfo, invalidateItems } from "@components/smart/smartSelect/SmartSelectAPI";
import { TSmartODataTableStorage } from "@components/smart/smartTable/SmartODataTableBase";
import { getRow } from "@components/smart/smartTable/SmartTable.utils";
import { IRow, TId } from "@components/table";
import BindingContext, { createBindingContext, IEntity } from "@odata/BindingContext";
import {
    CorrectiveInvoiceReceivedEntity,
    IDocumentEntity,
    IInvoiceIssuedEntity,
    IInvoiceReceivedEntity,
    InvoiceIssuedEntity,
    InvoiceReceivedEntity
} from "@odata/GeneratedEntityTypes";
import { ClearedStatusCode, DocumentTypeCode, PostedStatusCode } from "@odata/GeneratedEnums";
import {
    areDocumentsApplicableForSameCorrectiveDoc,
    clearOriginalDocumentSwitchPath,
    fetchAndApplyCorrectedDocuments,
    TCorrectiveDocumentEntity
} from "@pages/documents/corrective/CommonCorrectiveSharedUtils";
import { getDefinition } from "@pages/documents/corrective/CorrectiveDocumentSharedPairDef";
import { setNotPostedStatusFilter } from "@pages/documents/Document.utils";
import { FieldType, RowAction } from "enums";
import React, { useCallback, useMemo } from "react";
import DateType from "types/Date";

import { FormStorage } from "../../../views/formView/FormStorage";


interface IProps extends ISmartFieldRenderProps, ISmartFieldEvents {
    storage: FormStorage,
    isReceivableDocType: boolean
}

const CorrectedDocumentsCustomMultiSelect: React.FC<IProps> = (props) => {
    const { storage, onSelectChange, onBlur, parentProps, isReceivableDocType, ...passProps } = props;

    const [lastAppliedCorrectedDocuments, setLastAppliedCorrectedDocuments] = React.useState<IDocumentEntity[] | null>(storage.data.origEntity.CorrectedDocuments || null);

    const entity = storage.data.entity as (IInvoiceIssuedEntity | IInvoiceReceivedEntity);
    const InvoiceEntity = isReceivableDocType ? InvoiceReceivedEntity : InvoiceIssuedEntity;
    const dataBindingContext = storage.data.bindingContext;

    // make it async autocomplete component
    const enhancedInfo = useMemo(() => ({
        ...props.info,
        type: FieldType.Autocomplete
    }), [props.info]);

    const headerData = useMemo((): IReadOnlyListItem[] => {
        const header = [
            {
                label: storage.getInfo(dataBindingContext.navigate(InvoiceEntity.NumberOurs)).label,
                value: entity.NumberOurs
            },
            {
                label: storage.getInfo(dataBindingContext.navigate(InvoiceEntity.DateIssued)).label,
                value: DateType.format(entity.DateIssued)
            }
        ];

        if (entity.DocumentTypeCode === DocumentTypeCode.CorrectiveInvoiceIssued) {
            return header.slice(1, 2);
        }
        return header;
    }, [storage, dataBindingContext, InvoiceEntity.NumberOurs, InvoiceEntity.DateIssued, entity.NumberOurs, entity.DateIssued, entity.DocumentTypeCode]);

    const updateOriginalDocumentSwitchValue = useCallback((correctedDocuments: IDocumentEntity[]): void => {
        if (correctedDocuments.length > 1) {
            storage.setValueByPath(clearOriginalDocumentSwitchPath, false);
        } else {
            const entity = storage.getEntity<TCorrectiveDocumentEntity>();
            const isCorrectedDocumentCleared = entity.CorrectedDocuments?.[0]?.ClearedStatusCode === ClearedStatusCode.Cleared;
            const isCorrectiveDocumentCleared = entity.ClearedStatusCode === ClearedStatusCode.Cleared;

            storage.setValueByPath(clearOriginalDocumentSwitchPath, !isCorrectedDocumentCleared && !isCorrectiveDocumentCleared);
        }
    }, [storage]);

    const handleChange = useCallback((args: IMultiSelectionChangeArgs) => {
        onSelectChange(args);
        if (args.triggerAdditionalTasks && lastAppliedCorrectedDocuments?.length && !args.selectedItems?.length) {
            // last item was removed -> refetch items to apply filter without BP and currency condition
            fetchItemsByInfo(storage, { ...props.info, type: FieldType.Autocomplete })
                .then(() => { setLastAppliedCorrectedDocuments([]); });
        }
    }, [lastAppliedCorrectedDocuments?.length, onSelectChange, props.info, storage]);

    const processCorrectedDocumentsChange = useCallback(async (correctedDocuments: IDocumentEntity[]) => {
        let wasChanged = false;
        wasChanged = lastAppliedCorrectedDocuments?.length !== correctedDocuments?.length || !!correctedDocuments?.find(doc => !lastAppliedCorrectedDocuments?.some(ldoc => ldoc.Id === doc.Id));

        if (wasChanged) {
            if (correctedDocuments.length > 0) {
                const result = await fetchAndApplyCorrectedDocuments(storage as FormStorage, correctedDocuments);
                if (!result) {
                    return false;
                }
            } else {
                storage.clearValue((parentProps.bindingContext));
            }
            updateOriginalDocumentSwitchValue(correctedDocuments);

            // invalidate items not to show invalid values based on previous selection
            invalidateItems([parentProps.bindingContext], storage);
        }
        setLastAppliedCorrectedDocuments(correctedDocuments.length > 0 ? [...correctedDocuments] : []);

        return true;
    }, [lastAppliedCorrectedDocuments, parentProps.bindingContext, storage, updateOriginalDocumentSwitchValue]);

    const handleBlur = useCallback(async (e: IInputOnBlurEvent) => {
        const correctedDocuments = storage.getEntity<TCorrectiveDocumentEntity>().CorrectedDocuments ?? [];
        processCorrectedDocumentsChange(correctedDocuments);
        onBlur?.(e);
    }, [storage, processCorrectedDocumentsChange, onBlur]);

    const handlePairing = useCallback(async (selected: BindingContext[], entities: IEntity[]) => {
        // set the value to storage to have the items selected in the dropdown after closing the pairing dialog
        storage.setValueByPath(CorrectiveInvoiceReceivedEntity.CorrectedDocuments, entities);
        const result = processCorrectedDocumentsChange(entities);

        storage.refreshFields();
        return result;
    }, [processCorrectedDocumentsChange, storage]);

    const getFirstSelectedRow = useCallback((tableStorage: TSmartODataTableStorage) => {
        const activeRows = tableStorage.tableAPI.getState()?.activeRows;

        if (activeRows.size === 0) {
            return null;
        }

        const firstRowKey = Array.from(activeRows)[0] as string;
        const rowBc = createBindingContext(firstRowKey, tableStorage.oData.metadata);

        return getRow(tableStorage.tableAPI.getState().rows, rowBc);
    }, []);

    // disable row action if some row is already selected,
    // and has different BusinessPartner,TransactionCurrency or ExchangeRate
    const isRowWithoutAction = useCallback((tableStorage: TSmartODataTableStorage, rowId: TId, action: RowAction, row: IRow): boolean => {
        const selectedRow = getFirstSelectedRow(tableStorage);

        const currentDocument = row.customData.entity as IDocumentEntity;
        const isNotPosted = currentDocument.PostedStatusCode === PostedStatusCode.NotPosted;

        if (!selectedRow) {
            return isNotPosted;
        }

        const selectedDocument = selectedRow.customData.entity as IDocumentEntity;

        return isNotPosted || !areDocumentsApplicableForSameCorrectiveDoc(selectedDocument, currentDocument);
    }, [getFirstSelectedRow]);

    const tableViewProps = useMemo(() => ({
        title: storage.t(`CorrectiveInvoices${isReceivableDocType ? "Received" : "Issued"}:TitleSingular`),
        secondaryTitle: storage.t(`CorrectiveInvoices${isReceivableDocType ? "Received" : "Issued"}:Form.SecondaryTitle`),
        headerData: headerData,
        confirmText: storage.t("Document:Pair.ToPair"),
        successMessage: storage.t("CorrectiveInvoicesShared:PairingDialog.SuccessSubtitle"),
        isRowWithoutAction,
        presetDefaultFilters: (tableStorage: TSmartODataTableStorage): void => {
            setNotPostedStatusFilter(tableStorage);
            tableStorage.applyFilters();
        },
        withoutMainToggle: true
    }), [headerData, isReceivableDocType, isRowWithoutAction, storage]);

    return (
        <Field label={props.info.label}
               isRequired={props.isRequired}
               isDisabled={props.isDisabled}
               isReadOnly={props.isReadOnly}
               name={props.name}
               auditTrailData={props.auditTrailData}>
            <SmartPairingMultiSelect storage={storage}
                                     bindingContext={parentProps.bindingContext}
                                     fieldInfo={enhancedInfo}
                                     auditTrailData={props.auditTrailData}
                                     {...passProps}
                                     onChange={handleChange}
                                     onBlur={handleBlur}
                                     onPair={handlePairing}
                                     withoutMainToggle={true}
                                     dialogTableViewProps={tableViewProps}
                                     getDefinition={getDefinition.bind(this, isReceivableDocType)}
                                     {...parentProps.fieldProps}
            />
        </Field>
    );
};

export default CorrectedDocumentsCustomMultiSelect;
